import React, { useCallback } from 'react';
import { arrayOf, func, shape } from 'prop-types';
import { isBefore, startOfTomorrow } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers';

// utils
import { useUserRolePermissions } from 'hooks';
import { AUTH_SEQUENCING_ADD_AUTH } from 'feature/loading-management/utils/const';

// Components
import { Box, Chip, Divider, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import EventIcon from '@mui/icons-material/Event';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import EmptyState from './EmptyState';
import DragAndDropButtonGroup from './DragAndDropButtonGroup';
import { withBottomPaddingStyles, withLeftPaddingStyles } from '../EditPanel.styles';

const FutureSequenced = props => {
  const { futureSequences, futureDate, onDateChange, onMove, onRemoveSeq } = props;
  const { isProductLoadingManage } = useUserRolePermissions();
  const theme = useTheme();

  const onDragEnd = useCallback(
    sequence => {
      onMove(sequence.source.index, sequence.destination.index);
    },
    [onMove]
  );

  return (
    <Box color="primary.dark" pt={3} px={3}>
      <Typography color="inherit" sx={withBottomPaddingStyles(theme)} variant="subtitle1">
        Future Sequence
      </Typography>

      {!futureSequences.length ? (
        <EmptyState icon={<EventIcon sx={{ fontSize: 32 }} />} text={AUTH_SEQUENCING_ADD_AUTH} />
      ) : (
        <>
          <Divider />

          <Box>
            <DatePicker
              disablePast
              id="future-date"
              label="Future Date"
              minDate={startOfTomorrow()}
              onChange={onDateChange}
              placeholder="mm/dd/yyyy"
              selectedDate={futureDate}
              shouldDisableDate={date => isBefore(date, startOfTomorrow())}
              slotProps={{
                textField: {
                  placeholder: 'mm/dd/yyyy',
                  sx: { width: 1 },
                },
              }}
              sx={{
                mt: 1,
                width: '100%',
              }}
              value={futureDate}
            />
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable">
                {provided => (
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {futureSequences.map(
                      ({ inventoryOwner, productAuthorizationId, supplierDirectedBlend }, idx) => {
                        return (
                          <Draggable
                            draggableId={`productAuthorizationId-${productAuthorizationId}${
                              supplierDirectedBlend ? '-SDB' : ''
                            }`}
                            index={idx}
                            isDragDisabled={!isProductLoadingManage}
                            key={`productAuthorizationId-${productAuthorizationId}${
                              supplierDirectedBlend ? '-SDB' : ''
                            }`}
                          >
                            {_provided => (
                              <div
                                ref={_provided.innerRef}
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {..._provided.draggableProps}
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {..._provided.dragHandleProps}
                              >
                                <Box
                                  alignItems="center"
                                  display="flex"
                                  justifyContent="space-between"
                                  py={1.5}
                                  width={1}
                                >
                                  <Box
                                    sx={{
                                      alignItems: 'center',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                    }}
                                  >
                                    <Tooltip
                                      enterTouchDelay={10}
                                      sx={{ width: '64px' }}
                                      title="This sequence applies to a future date"
                                    >
                                      <Chip label="FUTURE" variant="badge" />
                                    </Tooltip>
                                    <Typography
                                      color="textPrimary"
                                      sx={withLeftPaddingStyles(theme)}
                                      variant="body2"
                                    >
                                      {supplierDirectedBlend
                                        ? 'Supplier Directed Blend'
                                        : inventoryOwner.nameShort}
                                    </Typography>
                                  </Box>
                                  {isProductLoadingManage && (
                                    <DragAndDropButtonGroup idx={idx} onRemoveSeq={onRemoveSeq} />
                                  )}
                                </Box>
                              </div>
                            )}
                          </Draggable>
                        );
                      }
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        </>
      )}
    </Box>
  );
};

export default FutureSequenced;

FutureSequenced.propTypes = {
  futureDate: shape({}),
  futureSequences: arrayOf(shape({})),
  onDateChange: func.isRequired,
  onMove: func.isRequired,
  onRemoveSeq: func.isRequired,
};

FutureSequenced.defaultProps = {
  futureDate: new Date(),
  futureSequences: [],
};
